import type Controller from '@ember/controller';
import { inject as controller } from '@ember/controller';
import Component from '@glimmer/component';
import { PrismBreadcrumb } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import SettingsRecommendationsDynamicRateRangesStrategyTable from 'lumen/components/settings/recommendations/dynamic-rate-ranges-strategy-table';
import SettingsRecommendationsStrategyCard from 'lumen/components/settings/recommendations/strategy-card';
import type { RecommendationStrategy } from 'lumen/models';
import type HotelSettingsRecommendationsDynamicRateRangesRoute from 'lumen/routes/hotel/settings/recommendations/dynamic-rate-ranges';
import type { ModelFrom } from 'lumen/utils/routes';

interface SettingsRecommendationsDynamicRateRangesSignature {
  Args: {
    model: ModelFrom<HotelSettingsRecommendationsDynamicRateRangesRoute>;
  };
}

export default class SettingsRecommendationsDynamicRateRanges extends Component<SettingsRecommendationsDynamicRateRangesSignature> {
  @controller('hotel.settings.recommendations')
  declare recommendationsController: Controller & {
    strategiesPerType: {
      dynamicRateRangeStrategies: RecommendationStrategy[];
    };
  };

  get defaultStrategy(): RecommendationStrategy | undefined {
    // There should be only one default strategy
    return this.dynamicRateRangeStrategies.find(
      (strategy) => strategy.isDefaultStrategy,
    );
  }

  get dynamicRateRangeStrategies(): RecommendationStrategy[] {
    return this.recommendationsController.strategiesPerType
      .dynamicRateRangeStrategies;
  }

  get seasonalStrategies(): RecommendationStrategy[] {
    return this.dynamicRateRangeStrategies.filter(
      (strategy) => !strategy.isDefaultStrategy,
    );
  }

  <template>
    <SettingsPageTemplate
      @description={{t
        "pricing_assistant.recommendations.settings.dynamic_rate_ranges.description"
      }}
      @title={{t
        "pricing_assistant.recommendations.settings.dynamic_rate_ranges.title"
      }}
      @type="container"
    >
      <:breadcrumb>
        <PrismBreadcrumb as |B|>
          <B.item
            @label={{t "pricing_assistant.recommendations.settings.main_title"}}
            @route="hotel.settings.recommendations.index"
          />
          <B.item
            @isCurrent={{true}}
            @label={{t
              "pricing_assistant.recommendations.settings.dynamic_rate_ranges.title"
            }}
          />
        </PrismBreadcrumb>
      </:breadcrumb>
      <:default>
        {{#if this.defaultStrategy}}
          <SettingsRecommendationsStrategyCard
            @initiallyOpened={{true}}
            @saveStrategyTask={{@model.saveStrategyTask}}
            @seasonalStrategies={{this.seasonalStrategies}}
            @strategy={{this.defaultStrategy}}
            @strategyComponent={{SettingsRecommendationsDynamicRateRangesStrategyTable}}
          />
        {{/if}}
      </:default>
    </SettingsPageTemplate>
  </template>
}
